import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const sectionRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formData = new FormData(e.target);

    try {
      const response = await fetch('https://formspree.io/f/xvzzbdkp', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitStatus('success');
        formRef.current?.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#0a0a0a]" ref={sectionRef}>
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 divider-elegant" />
      
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#c9a87c]/5 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div 
          className={`text-center mb-16 ${
            isVisible ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-[#c9a87c] mb-4 block">
            Get Started
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#faf9f7] mb-6">
            Schedule Your <span className="text-gradient-gold">Free Consultation</span>
          </h2>
          <p className="font-sans text-lg text-[#faf9f7]/60 max-w-2xl mx-auto">
            Ready to protect your Florida home? Contact us today to discuss how a Lady Bird Deed can benefit you and your family.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div 
            className={`lg:col-span-2 ${
              isVisible ? 'animate-slide-in-left' : 'opacity-0'
            }`}
          >
            <div className="space-y-8">
              {/* Tagline */}
              <div>
                <h3 className="font-serif text-2xl text-[#faf9f7] mb-4">Let's Discuss Your Estate Plan</h3>
                <p className="font-sans text-[#faf9f7]/60 leading-relaxed">
                  Every Florida homeowner's situation is unique. Reach out to discover if a Lady Bird Deed 
                  is the right solution for protecting your home and your family's future.
                </p>
              </div>

              {/* Contact details */}
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-[#141414] rounded-lg border border-[#c9a87c]/10">
                  <div className="w-10 h-10 rounded-lg bg-[#c9a87c]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#c9a87c]" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-[#faf9f7]/50">Location</p>
                    <p className="font-sans text-[#faf9f7]">Florida</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#141414] rounded-lg border border-[#c9a87c]/10">
                  <div className="w-10 h-10 rounded-lg bg-[#c9a87c]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#c9a87c]" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-[#faf9f7]/50">Email</p>
                    <p className="font-sans text-[#faf9f7]">Contact via form</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#141414] rounded-lg border border-[#c9a87c]/10">
                  <div className="w-10 h-10 rounded-lg bg-[#c9a87c]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#c9a87c]" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-[#faf9f7]/50">Phone</p>
                    <a href="tel:941-206-2223" className="font-sans text-[#faf9f7] hover:text-[#c9a87c] transition-colors">(941) 206-2223</a>
                  </div>
                </div>
              </div>

              {/* Trust message */}
              <div className="p-6 bg-gradient-to-br from-[#c9a87c]/10 to-transparent border border-[#c9a87c]/20 rounded-lg">
                <p className="font-serif text-lg text-[#faf9f7] italic">
                  "Protecting your home means protecting your family's future. Let me help you do it right."
                </p>
                <p className="font-sans text-sm text-[#c9a87c] mt-2">- Season A. Spanski</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div 
            className={`lg:col-span-3 ${
              isVisible ? 'animate-slide-in-right' : 'opacity-0'
            }`}
          >
            <form 
              ref={formRef}
              onSubmit={handleSubmit} 
              className="bg-gradient-to-br from-[#141414] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 lg:p-10"
            >
              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 rounded-lg flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <p className="font-sans text-green-400">
                    Thank you! Your message has been sent. We'll be in touch soon.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="font-sans text-red-400">
                    Something went wrong. Please try again or contact us directly.
                  </p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block font-sans text-sm text-[#faf9f7]/70 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="John Smith"
                    className="input-elegant bg-[#1a1a1a] border-[#c9a87c]/20 text-[#faf9f7] placeholder:text-[#faf9f7]/30 h-12"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block font-sans text-sm text-[#faf9f7]/70 mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="john@example.com"
                    className="input-elegant bg-[#1a1a1a] border-[#c9a87c]/20 text-[#faf9f7] placeholder:text-[#faf9f7]/30 h-12"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block font-sans text-sm text-[#faf9f7]/70 mb-2">
                    Phone Number
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    className="input-elegant bg-[#1a1a1a] border-[#c9a87c]/20 text-[#faf9f7] placeholder:text-[#faf9f7]/30 h-12"
                  />
                </div>

                {/* Property Location */}
                <div>
                  <label htmlFor="location" className="block font-sans text-sm text-[#faf9f7]/70 mb-2">
                    Property Location (County)
                  </label>
                  <Input
                    id="location"
                    name="location"
                    type="text"
                    placeholder="Palm Beach County"
                    className="input-elegant bg-[#1a1a1a] border-[#c9a87c]/20 text-[#faf9f7] placeholder:text-[#faf9f7]/30 h-12"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="mb-8">
                <label htmlFor="message" className="block font-sans text-sm text-[#faf9f7]/70 mb-2">
                  Tell us about your situation
                </label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  placeholder="Briefly describe your property and estate planning goals..."
                  className="input-elegant bg-[#1a1a1a] border-[#c9a87c]/20 text-[#faf9f7] placeholder:text-[#faf9f7]/30 resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-elegant bg-[#c9a87c] hover:bg-[#d4b896] text-[#0a0a0a] font-sans font-semibold tracking-wide h-14 text-base rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Request Free Consultation
                  </span>
                )}
              </Button>

              {/* Privacy note */}
              <p className="font-sans text-xs text-[#faf9f7]/40 text-center mt-4">
                Your information is confidential and protected by attorney-client privilege.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
